/* Notification Center — shared Recipients editor.
 *
 * Used by BOTH the Rule Detail → Recipients tab (view + edit) and the New Rule
 * wizard → Recipients step, so the two can never drift. Recipients load from the
 * live Users data source (distinct Role + Department, with the concatenated user
 * names for each pair); the editor renders the current recipient rows (each with
 * In-App / Email / SMS channel toggles) and, in edit mode, the "Add recipients
 * from Users" grid — Checkbox | Role | Department | Users | Notify Via — with
 * fuzzy search, multi-select + Add selected, and duplicate prevention.
 *
 * CONTROLLED: the parent owns `recipients` (NcRecipient[]) — the exact structure
 * persisted in the rule config — and receives every change via onChange. The
 * picker's own transient state (role options, checked rows, per-row channels,
 * search) lives here. */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify } from '../../components/notifications';
import { getRecipientRoles, type RecipientRole } from './api';
import { RECIPIENT_KIND_LABEL, groupUserName, type NcRecipient, type NcGroupUser } from './data';

/** Recipient identity = the ROLE only. A role appears ONCE in the grid with its
 *  departments + users combined, so two departments never make two recipient
 *  rows; the `dept` arg is accepted (call-site compatibility) but ignored. */
export const recipKey = (name: string, _dept?: string | null) => name;

/** One grid row: a role with all its departments + users combined. */
interface GroupedRole { role: string; departments: string[]; users: string[]; }

/** Avatar initials for a recipient name. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

type Chan = { inApp: boolean; email: boolean; sms: boolean };
const DEFAULT_CHAN: Chan = { inApp: true, email: true, sms: false };

export function RecipientsEditor({ recipients, onChange, readOnly = false, immediateToggle = false, subrowsOnly = false, users = [], onUsersChange, renderAddSection }: {
  recipients: NcRecipient[];
  /** Called with the next recipient list on every add / remove / channel change. */
  onChange: (next: NcRecipient[]) => void;
  /** View mode — rows are static (no remove / channel toggle) and the add grid is hidden. */
  readOnly?: boolean;
  /** The add-grid checkbox DIRECTLY adds/removes the role from the members list
   *  (no "Add selected" button). Opt-in — the Recipient Group Edit uses it; the
   *  Notification Rules Recipients step keeps its check-then-Add-selected flow. */
  immediateToggle?: boolean;
  /** Render ONLY the "Role / Department | Users | Notify Via" sub-rows of every
   *  member in one table — no per-Role main row. Opt-in — used by the Rule Detail
   *  → Recipients group expansion and the Recipient Group View + Edit. When
   *  editable, the role's In-App / Email / SMS toggles + remove sit in ONE cell
   *  that spans that role's department rows: channels are stored per ROLE, so a
   *  per-department toggle would misrepresent the data. */
  subrowsOnly?: boolean;
  /** Individually selected users of a Recipient Group. With subrowsOnly they are
   *  listed in the SAME Selected Members table, after the role rows (Role/Department
   *  = their roles + department, Users = the username), with their own channels and
   *  Remove — so the table is the one list of everyone the group notifies. */
  users?: NcGroupUser[];
  /** Called when a user row's channel is toggled or the user is removed (edit). */
  onUsersChange?: (next: NcGroupUser[]) => void;
  /** Edit only: wraps the roles add-grid, so the caller can place it in the
   *  "Add Recipients" section beside other sources (the Roles | Users tabs). When
   *  absent the add-grid renders as before, straight after the selected table. */
  renderAddSection?: (rolesAddGrid: ReactNode) => ReactNode;
}) {
  const notify = useNotify();
  const editable = !readOnly;
  // Distinct Role + Department options from the live Users data source.
  const [roleOptions, setRoleOptions] = useState<RecipientRole[]>([]);
  const [rolePick, setRolePick] = useState<Record<string, boolean>>({});
  const [roleChan, setRoleChan] = useState<Record<string, Chan>>({});
  const [roleSearch, setRoleSearch] = useState('');
  // Expand/collapse state for the master/detail members grid (collapsed by default).

  useEffect(() => {
    // Load the Users datasource for the add-grid (edit) OR the sub-rows.
    if (readOnly && !subrowsOnly) return;
    let alive = true;
    getRecipientRoles().then((rows) => { if (alive) setRoleOptions(rows); }).catch(() => { if (alive) setRoleOptions([]); });
    return () => { alive = false; };
  }, [readOnly, subrowsOnly]);

  // Read-only View subrows: one line per saved Role + Department, "Role - Dept:
  // Users" (users only for that exact pair; "—" when none). A saved member's
  // Department may combine several depts — split it and resolve each; if the role
  // isn't in the Users data at all, fall back to the saved dept string with "—".
  function subrowsFor(r: NcRecipient): { key: string; label: string; users: string }[] {
    const matches = roleOptions.filter((o) => o.role === r.name);
    if (matches.length) {
      return matches.map((o, i) => {
        const dept = (o.department ?? '').trim();
        return { key: `${r.name}|${dept}|${i}`, label: dept ? `${r.name} - ${dept}` : r.name, users: (o.users ?? '').trim() || '—' };
      });
    }
    const depts = (r.department ?? '').split(',').map((d) => d.trim()).filter(Boolean);
    if (depts.length) return depts.map((d, i) => ({ key: `${r.name}|${d}|${i}`, label: `${r.name} - ${d}`, users: '—' }));
    return [{ key: r.name, label: r.name, users: '—' }];
  }

  // Group the Users-sourced Role+Department options into ONE row per role, with
  // its departments and (deduped) users combined.
  const grouped = useMemo<GroupedRole[]>(() => {
    const map = new Map<string, GroupedRole>();
    for (const o of roleOptions) {
      let g = map.get(o.role);
      if (!g) { g = { role: o.role, departments: [], users: [] }; map.set(o.role, g); }
      const dept = (o.department ?? '').trim();
      if (dept && !g.departments.includes(dept)) g.departments.push(dept);
      for (const u of (o.users ?? '').split(',').map((s) => s.trim()).filter(Boolean)) if (!g.users.includes(u)) g.users.push(u);
    }
    return [...map.values()].sort((a, b) => a.role.localeCompare(b.role));
  }, [roleOptions]);

  const existingRoleKeys = useMemo(() => new Set(recipients.map((r) => r.name)), [recipients]);
  const chanOf = (key: string): Chan => roleChan[key] ?? DEFAULT_CHAN;
  const togglePick = (key: string) => setRolePick((cur) => ({ ...cur, [key]: !cur[key] }));
  const toggleRowChan = (key: string, ch: keyof Chan) =>
    setRoleChan((cur) => { const c = cur[key] ?? DEFAULT_CHAN; return { ...cur, [key]: { ...c, [ch]: !c[ch] } }; });

  function removeRecipient(id: string) { onChange(recipients.filter((r) => r.id !== id)); }
  function toggleChannel(id: string, ch: keyof Chan) {
    onChange(recipients.map((r) => (r.id === id ? { ...r, [ch]: !r[ch] } : r)));
  }
  // Add every checked, not-already-present ROLE (departments + users combined)
  // with its Notify Via channels. A checked row with NO channel is skipped with a
  // warning (each added recipient must have at least one channel).
  function addSelectedRoles() {
    const picks = grouped.filter((g) => rolePick[g.role] && !existingRoleKeys.has(g.role));
    const invalid = picks.filter((g) => { const c = chanOf(g.role); return !c.inApp && !c.email && !c.sms; });
    const valid = picks.filter((g) => { const c = chanOf(g.role); return c.inApp || c.email || c.sms; });
    if (invalid.length) notify.warning(`Select at least one Notify Via channel for: ${invalid.map((g) => g.role).join(', ')}`);
    if (!valid.length) return;
    onChange([...recipients, ...valid.map((g, i) => {
      const c = chanOf(g.role);
      const dept = g.departments.join(', ');
      return { id: `r-${Date.now()}-${i}-${g.role}`, name: g.role, kind: 'role' as const, department: dept || undefined, desc: dept, inApp: c.inApp, email: c.email, sms: c.sms };
    })]);
    setRolePick({});
  }

  // Immediate mode (Recipient Group Edit): the grid checkbox add/removes the role
  // straight away. Add uses the row's current Notify Via (defaults In-App+Email);
  // remove drops it. Duplicate roles can't occur — membership is keyed by role.
  function toggleMembership(g: GroupedRole) {
    if (existingRoleKeys.has(g.role)) { onChange(recipients.filter((r) => r.name !== g.role)); return; }
    const c = chanOf(g.role);
    const dept = g.departments.join(', ');
    onChange([...recipients, { id: `r-${Date.now()}-${g.role}`, name: g.role, kind: 'role' as const, department: dept || undefined, desc: dept, inApp: c.inApp, email: c.email, sms: c.sms }]);
  }
  // Notify Via toggle in immediate mode: edit the added recipient's channel, or,
  // for a not-yet-added role, stage the channel (used when it's checked in).
  function toggleRowChanImmediate(g: GroupedRole, ch: keyof Chan) {
    if (existingRoleKeys.has(g.role)) { onChange(recipients.map((r) => (r.name === g.role ? { ...r, [ch]: !r[ch] } : r))); return; }
    toggleRowChan(g.role, ch);
  }

  const q = roleSearch.trim().toLowerCase();
  const rows = grouped.filter((g) => !q || g.role.toLowerCase().includes(q) || g.departments.join(' ').toLowerCase().includes(q) || g.users.join(' ').toLowerCase().includes(q));
  const pickedCount = grouped.filter((g) => rolePick[g.role] && !existingRoleKeys.has(g.role)).length;

  return (
    <>
      {recipients.length === 0 && users.length === 0 && (
        <div className="nc-form-hint" style={{ margin: '4px 0 10px' }}>No recipients configured{editable ? ' — add one below.' : '.'}</div>
      )}
      {/* Sub-rows only: the SAME Role/Department sub-rows, flattened across the
          roles — no intermediate Role main row. Data is unchanged (same
          subrowsFor). View: Notify Via as text per row. Edit: the role's channel
          toggles + remove in one cell spanning that role's department rows. */}
      {(recipients.length > 0 || users.length > 0) && subrowsOnly && (
        <div className="nc-sub-wrap">
          <table className={`nc-sub-table${editable ? ' nc-sub-table--edit' : ''}`}>
            <thead><tr>
              <th style={{ width: '38%' }}>Role / Department</th>
              <th>Users</th>
              <th style={{ width: editable ? 200 : 140 }}>Notify Via</th>
              {editable && <th style={{ width: 64, textAlign: 'center' }}>Remove</th>}
            </tr></thead>
            <tbody>
              {recipients.flatMap((r) => {
                const chanText = [r.inApp && 'In-App', r.email && 'Email', r.sms && 'SMS'].filter(Boolean).join(', ') || '—';
                const subs = subrowsFor(r);
                return subs.map((s, i) => (
                  <tr key={`${r.id}|${s.key}`}>
                    <td><span className="nc-limit-type">{s.label}</span></td>
                    <td>{s.users}</td>
                    {!editable && <td>{chanText}</td>}
                    {editable && i === 0 && (
                      <td className="nc-sub-chancell" rowSpan={subs.length}>
                        <span className="nc-sub-chans">
                          <button type="button" className={`nc-chan nc-chan-btn${r.inApp ? ' on' : ''}`} aria-pressed={r.inApp} title={`${r.name}: IN-APP ${r.inApp ? 'on' : 'off'}`} onClick={() => toggleChannel(r.id, 'inApp')}>IN-APP</button>
                          <button type="button" className={`nc-chan nc-chan-btn${r.email ? ' on' : ''}`} aria-pressed={r.email} title={`${r.name}: EMAIL ${r.email ? 'on' : 'off'}`} onClick={() => toggleChannel(r.id, 'email')}>EMAIL</button>
                          <button type="button" className={`nc-chan nc-chan-btn${r.sms ? ' on' : ''}`} aria-pressed={!!r.sms} title={`${r.name}: SMS ${!!r.sms ? 'on' : 'off'}`} onClick={() => toggleChannel(r.id, 'sms')}>SMS</button>
                        </span>
                      </td>
                    )}
                    {editable && i === 0 && (
                      <td className="nc-sub-span" rowSpan={subs.length}>
                        <button className="nc-recip-del" title={subs.length > 1 ? `Remove ${r.name} (all its departments)` : `Remove ${r.name}`} onClick={() => removeRecipient(r.id)}><i className="ti ti-x" /></button>
                      </td>
                    )}
                  </tr>
                ));
              })}
              {/* Individually selected users — after the roles, one row each:
                  Role / Department = the user's roles + department, Users = the
                  username. Same channel toggles and Remove as a role row. */}
              {users.map((u) => {
                const label = [u.roles, u.department].filter(Boolean).join(' - ') || '—';
                const name = groupUserName(u);
                const userChanText = [u.inApp && 'In-App', u.email && 'Email', u.sms && 'SMS'].filter(Boolean).join(', ') || '—';
                const toggleUser = (ch: 'inApp' | 'email' | 'sms') =>
                  onUsersChange?.(users.map((x) => (x.userId === u.userId ? { ...x, [ch]: !x[ch] } : x)));
                return (
                  <tr key={`user|${u.userId}`} className="nc-sub-userrow">
                    <td title="Individually selected user">
                      <i className="ti ti-user nc-sub-usericon" aria-hidden /> <span className="nc-limit-type">{label}</span>
                    </td>
                    <td>
                      {name}
                      {u.exists === false && <span className="nc-role-nodept"> · user no longer exists</span>}
                    </td>
                    {!editable && <td>{userChanText}</td>}
                    {editable && (
                      <td className="nc-sub-chancell">
                        <span className="nc-sub-chans">
                          <button type="button" className={`nc-chan nc-chan-btn${u.inApp ? ' on' : ''}`} aria-pressed={u.inApp} title={`${name}: IN-APP ${u.inApp ? 'on' : 'off'}`} onClick={() => toggleUser('inApp')}>IN-APP</button>
                          <button type="button" className={`nc-chan nc-chan-btn${u.email ? ' on' : ''}`} aria-pressed={u.email} title={`${name}: EMAIL ${u.email ? 'on' : 'off'}`} onClick={() => toggleUser('email')}>EMAIL</button>
                          <button type="button" className={`nc-chan nc-chan-btn${u.sms ? ' on' : ''}`} aria-pressed={u.sms} title={`${name}: SMS ${u.sms ? 'on' : 'off'}`} onClick={() => toggleUser('sms')}>SMS</button>
                        </span>
                      </td>
                    )}
                    {editable && (
                      <td className="nc-sub-span">
                        <button className="nc-recip-del" title={`Remove ${name}`} onClick={() => onUsersChange?.(users.filter((x) => x.userId !== u.userId))}><i className="ti ti-x" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Flat recipient rows (channel chips + remove in edit) — callers without subrowsOnly. */}
      {recipients.length > 0 && !subrowsOnly && (
      <div className="nc-recip-list">
      {recipients.map((r) => (
        <div key={r.id} className="nc-recip-row">
          <div className="nc-recip-left"><div className="nc-recip-av nc-recip-badge">{initialsOf(r.name)}</div><div>
            <div className="nc-recip-name">{r.name}</div>
            <div className="nc-recip-type"><span className={`nc-rt ${r.kind}`}>{RECIPIENT_KIND_LABEL[r.kind]}</span> {r.department ? r.department : r.desc}</div>
          </div></div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className={`nc-chan${r.inApp ? ' on' : ''}${editable ? ' nc-chan-btn' : ''}`} role={editable ? 'button' : undefined} onClick={editable ? () => toggleChannel(r.id, 'inApp') : undefined}>IN-APP</span>
            <span className={`nc-chan${r.email ? ' on' : ''}${editable ? ' nc-chan-btn' : ''}`} role={editable ? 'button' : undefined} onClick={editable ? () => toggleChannel(r.id, 'email') : undefined}>EMAIL</span>
            <span className={`nc-chan${r.sms ? ' on' : ''}${editable ? ' nc-chan-btn' : ''}`} role={editable ? 'button' : undefined} onClick={editable ? () => toggleChannel(r.id, 'sms') : undefined}>SMS</span>
            {editable && <button className="nc-recip-del" title="Remove recipient" onClick={() => removeRecipient(r.id)}><i className="ti ti-x" /></button>}
          </div>
        </div>
      ))}
      </div>
      )}

      {editable && (renderAddSection ? renderAddSection(rolesAddGrid()) : rolesAddGrid())}
    </>
  );

  /** The roles add-grid (Checkbox | Role | Department | Users | Notify Via). */
  function rolesAddGrid() {
    return (
        <div className="nc-role-add">
          <div className="nc-role-add-hd">
            <span>{renderAddSection ? 'Available roles' : 'Add recipients from Users'}</span>
            <input className="nc-finput nc-sm" placeholder="Search role / department…" value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} style={{ maxWidth: 240 }} />
          </div>
          <div className="nc-role-tblwrap">
            <table className="nc-tgt-table nc-role-tbl">
              <thead><tr><th style={{ width: 40, textAlign: 'center' }}></th><th>Role</th><th>Department</th><th>Users</th><th style={{ width: 210 }}>Notify Via</th></tr></thead>
              <tbody>
                {grouped.length === 0 && <tr><td colSpan={5} className="nc-role-empty">No roles found in Users data.</td></tr>}
                {grouped.length > 0 && rows.length === 0 && <tr><td colSpan={5} className="nc-role-empty">No roles match “{roleSearch}”.</td></tr>}
                {rows.map((g) => {
                  const key = g.role;
                  const already = existingRoleKeys.has(key);
                  const addedRecip = already ? recipients.find((r) => r.name === key) : undefined;
                  const chan = addedRecip ? { inApp: addedRecip.inApp, email: addedRecip.email, sms: !!addedRecip.sms } : chanOf(key);
                  // Immediate mode: the checkbox = membership (existing rows stay
                  // uncheckable-to-remove, not disabled/dimmed) and channels are live.
                  const boxChecked = immediateToggle ? already : (already || !!rolePick[key]);
                  const boxDisabled = immediateToggle ? false : already;
                  const chanDisabled = immediateToggle ? false : already;
                  const onBox = () => (immediateToggle ? toggleMembership(g) : togglePick(key));
                  const onRow = immediateToggle ? () => toggleMembership(g) : (already ? undefined : () => togglePick(key));
                  const onChan = (ch: keyof Chan) => (immediateToggle ? toggleRowChanImmediate(g, ch) : toggleRowChan(key, ch));
                  return (
                    <tr key={key} className={!immediateToggle && already ? 'nc-role-already' : ''} onClick={onRow} style={{ cursor: onRow ? 'pointer' : 'default' }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={boxChecked} disabled={boxDisabled} onClick={(e) => e.stopPropagation()} onChange={onBox} />
                      </td>
                      <td>{g.role}{already && <span className="nc-oc-off"> · added</span>}</td>
                      <td>{g.departments.length ? g.departments.join(', ') : <span className="nc-role-nodept">—</span>}</td>
                      <td>{g.users.length ? g.users.join(', ') : <span className="nc-role-nodept">—</span>}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="nc-notify-via">
                          {([['inApp', 'In App'], ['email', 'Email'], ['sms', 'SMS']] as const).map(([ch, lbl]) => (
                            <label key={ch} className="nc-nv-check"><input type="checkbox" checked={chan[ch]} disabled={chanDisabled} onChange={() => onChan(ch)} /> {lbl}</label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="nc-role-add-ft">
            {immediateToggle ? (
              <span className="nc-role-add-hint">Check a role to add it to the group; uncheck to remove.</span>
            ) : (
              <>
                <span className="nc-role-add-hint">{pickedCount ? `${pickedCount} selected` : 'Check one or more roles to add'}</span>
                <button className="ca-btn ca-btn-solid" disabled={!pickedCount} onClick={addSelectedRoles}><span className="tb-i">＋</span> Add selected</button>
              </>
            )}
          </div>
        </div>
    );
  }
}
